<template>
  <!-- закрывается только кнопками: клик по фону и Esc не закрывают -->
  <v-dialog
    :value="value"
    max-width="720"
    persistent
    @input="$emit('input', $event)"
  >
    <div class="iz">
      <v-btn
        icon
        class="iz-close"
        aria-label="Закрыть"
        :disabled="loading"
        @click="$emit('input', false)"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <h3>Импорт карты</h3>

      <div class="iz-steps">
        <div
          v-for="(s, i) in steps"
          :key="s"
          class="iz-step"
          :class="{ on: step === i + 1, done: step > i + 1 }"
        >
          <span class="n">{{ i + 1 }}</span>{{ s }}
        </div>
      </div>

      <v-alert
        v-if="error"
        dense
        text
        type="error"
        class="iz-alert"
      >
        {{ error }}
      </v-alert>

      <!-- 1. Параметры и файл -->
      <div v-if="step === 1" class="iz-body">
        <div class="iz-label">
          Формат файла
        </div>
        <zone-format-picker v-model="format" />

        <div class="iz-label">
          Какие филиалы обновлять
        </div>
        <filials-picker v-model="scope" :filials="filials" />

        <div class="iz-label">
          Файл KML из Яндекс.Конструктора
        </div>
        <div
          class="iz-drop"
          :class="{ over: dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
        >
          <v-file-input
            v-model="file"
            accept=".kml"
            placeholder="Выберите файл .kml"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            outlined
            dense
            show-size
            hide-details
            class="iz-file"
          />
          <div class="iz-hint">
            или перетащите файл сюда
          </div>
        </div>
      </div>

      <!-- 2. Разбор файла -->
      <div v-else-if="step === 2 && plan" class="iz-body">
        <div class="iz-stats">
          <div v-for="s in stats" :key="s.label" class="iz-stat">
            <b>{{ s.value }}</b>
            <span>{{ s.label }}</span>
          </div>
        </div>

        <div v-if="warningGroups.length" class="iz-label">
          Замечания к файлу
        </div>
        <v-expansion-panels v-if="warningGroups.length" multiple accordion>
          <v-expansion-panel v-for="g in warningGroups" :key="g.code">
            <v-expansion-panel-header>
              <span class="iz-warn">{{ g.text }} <b>{{ g.items.length }}</b></span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(w, i) in g.items" :key="i" class="iz-warn-item">
                {{ w.name || '(без названия)' }}
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!-- 3. Конфликты -->
      <div v-else-if="step === 3 && plan" class="iz-body">
        <div v-if="conflictTypes.length > 1" class="iz-substeps">
          {{ conflictIndex + 1 }} из {{ conflictTypes.length }}
        </div>
        <import-conflicts-step
          v-model="resolutions"
          :type="conflictType"
          :items="conflictItems(conflictType)"
          :rows-by-key="rowsByKey"
          :filials="filials"
          :scope-filials="scopeFilials"
          :unresolved="unresolvedByKey"
        />
      </div>

      <!-- 4. Итог -->
      <div v-else-if="step === 4 && final" class="iz-body">
        <import-summary-step v-model="deleteChecked" :final="final" :filials="filials" />
      </div>

      <!-- 5. Готово -->
      <div v-else-if="step === 5 && result" class="iz-body iz-done">
        <v-icon color="success" size="48">
          mdi-check-circle-outline
        </v-icon>
        <div class="iz-done-title">
          Импорт выполнен
        </div>
        <div class="iz-done-text">
          Обновлено {{ result.updated }}, добавлено {{ result.created }}, удалено {{ result.deleted }}.
          Без изменений {{ result.unchanged }}, пропущено {{ result.skipped }}.
        </div>
      </div>

      <div class="iz-actions">
        <v-btn
          v-if="step === 1 || step === 5"
          depressed
          outlined
          class="iz-btn"
          @click="$emit('input', false)"
        >
          {{ step === 5 ? 'Закрыть' : 'Отмена' }}
        </v-btn>
        <v-btn
          v-else
          depressed
          outlined
          class="iz-btn"
          :disabled="loading"
          @click="back"
        >
          Назад
        </v-btn>

        <v-btn
          v-if="step === 1"
          depressed
          color="primary"
          class="iz-btn"
          :loading="loading"
          :disabled="!canCheck"
          @click="check"
        >
          Проверить файл
        </v-btn>
        <v-btn
          v-else-if="step === 2 || step === 3"
          depressed
          color="primary"
          class="iz-btn"
          :loading="loading"
          :disabled="step === 3 && !stepDecided"
          @click="next"
        >
          {{ nextText }}
        </v-btn>
        <template v-else-if="step === 4">
          <v-btn
            v-if="stale"
            depressed
            color="primary"
            class="iz-btn"
            :loading="loading"
            @click="check"
          >
            Проверить файл заново
          </v-btn>
          <v-btn
            v-else
            depressed
            color="primary"
            class="iz-btn"
            :loading="loading"
            :disabled="!canApply"
            @click="apply"
          >
            Импортировать
          </v-btn>
        </template>
        <v-btn
          v-else-if="step === 5"
          depressed
          color="primary"
          class="iz-btn"
          @click="openHistory"
        >
          Открыть историю
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
  import { mapActions } from 'vuex'
  import FilialsPicker from '@/components/geo/delivery-zones/FilialsPicker.vue'
  import ZoneFormatPicker from '@/components/geo/delivery-zones/ZoneFormatPicker.vue'
  import ImportConflictsStep from '@/components/geo/delivery-zones/import/ImportConflictsStep.vue'
  import ImportSummaryStep from '@/components/geo/delivery-zones/import/ImportSummaryStep.vue'
  import {
    CONFLICT_ORDER,
    conflictKeys,
    isDecided,
  } from '@/components/geo/delivery-zones/import/importResolutions'
  import { KML_WARNINGS } from '@/util/zonesKml'

  /**
   * Пошаговый импорт зон из KML: файл → разбор → конфликты → итог → готово.
   * Сервер держит план 30 минут по токену; ничего не меняется до кнопки «Импортировать».
   */
  export default {
    name: 'ImportZonesWizard',
    components: { FilialsPicker, ZoneFormatPicker, ImportConflictsStep, ImportSummaryStep },
    props: {
      value: { type: Boolean, default: false },
      filials: { type: Array, default: () => [] },
      currentFilial: { type: [String, Number], default: 'all' },
    },
    data: () => ({
      steps: ['Файл', 'Разбор', 'Конфликты', 'Итог', 'Готово'],
      step: 1,
      format: 'fakt',
      scope: { scope: 'all', filialIds: [] },
      file: null,
      dragOver: false,
      loading: false,
      error: '',
      plan: null,
      resolutions: {},
      conflictIndex: 0,
      unresolvedByKey: {},
      extraNameTaken: [],
      final: null,
      deleteChecked: {},
      stale: false,
      result: null,
    }),
    computed: {
      canCheck () {
        return !!this.file && (this.scope.scope === 'all' || this.scope.filialIds.length > 0)
      },
      rowsByKey () {
        const map = {}
        ;(this.plan ? this.plan.rows : []).forEach((r) => {
          map[r.key] = r
        })
        return map
      },
      scopeFilials () {
        return this.scope.scope === 'all'
          ? this.filials
          : this.filials.filter((f) => this.scope.filialIds.includes(f.id))
      },
      conflictTypes () {
        return CONFLICT_ORDER.filter((t) => this.conflictItems(t).length > 0)
      },
      conflictType () {
        return this.conflictTypes[this.conflictIndex]
      },
      stepDecided () {
        if (this.step !== 3 || !this.conflictType) return true
        return this.conflictItems(this.conflictType).every((item) =>
          isDecided(this.conflictType, item, this.resolutions),
        )
      },
      nextText () {
        if (this.step === 2 && this.conflictTypes.length) return 'Разобрать конфликты'
        if (this.step === 3 && this.conflictIndex < this.conflictTypes.length - 1) return 'Далее'
        return 'К итогу'
      },
      stats () {
        const s = this.plan.summary
        return [
          { label: 'объектов в файле', value: s.placemarks },
          { label: 'зон-полигонов', value: s.polygons },
          { label: 'точек филиалов (не импортируются)', value: s.points },
          { label: 'пропущено: битая геометрия', value: s.skippedGeometry },
          { label: 'вне выбранных филиалов', value: s.outOfScope },
          { label: 'совпали с зонами', value: s.update },
          { label: 'новых', value: s.create },
          { label: 'конфликтов', value: Object.values(s.conflicts).reduce((a, b) => a + b, 0) },
        ]
      },
      warningGroups () {
        const groups = {}
        ;(this.plan ? this.plan.warnings : []).forEach((w) => {
          groups[w.code] = groups[w.code] || { code: w.code, text: KML_WARNINGS[w.code] || w.code, items: [] }
          groups[w.code].items.push(w)
        })
        return Object.values(groups)
      },
      canApply () {
        const t = this.final ? this.final.totals : null
        return !!t && (t.update + t.create + Object.values(this.deleteChecked).filter(Boolean).length) > 0
      },
    },
    watch: {
      value (open) {
        if (open) this.reset()
      },
    },
    methods: {
      ...mapActions('polygons', ['importPreview', 'importResolve', 'importApply']),
      reset () {
        Object.assign(this, {
          step: 1,
          file: null,
          error: '',
          plan: null,
          resolutions: {},
          conflictIndex: 0,
          unresolvedByKey: {},
          extraNameTaken: [],
          final: null,
          deleteChecked: {},
          stale: false,
          result: null,
          scope:
            this.currentFilial === 'all'
              ? { scope: 'all', filialIds: [] }
              : { scope: 'filials', filialIds: [this.currentFilial] },
        })
      },
      onDrop (e) {
        this.dragOver = false
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
        if (f) this.file = f
      },
      // элементы конфликта типа: из плана + «имя занято», которое нашёл разбор решений
      conflictItems (type) {
        if (!this.plan) return []
        const items = this.plan.conflicts[type] || []
        if (type !== 'name_taken') return items
        const known = new Set(items.map((i) => i.key))
        return [...items, ...this.extraNameTaken.filter((i) => !known.has(i.key))]
      },
      // решения по умолчанию: дубль - первый обновляет, остальные новые; имя - подсказка сервера
      defaultResolutions (plan) {
        const res = {}
        ;(plan.conflicts.duplicate || []).forEach((g) => {
          g.keys.slice(1).forEach((k) => {
            res[k] = { asNew: true }
          })
        })
        ;(plan.conflicts.name_taken || []).forEach((c) => {
          res[c.key] = { ...(res[c.key] || {}), name: c.suggestion }
        })
        return res
      },
      async check () {
        this.loading = true
        this.error = ''
        this.stale = false
        try {
          const plan = await this.importPreview({
            file: this.file,
            format: this.format,
            scope: this.scope.scope,
            filialIds: this.scope.filialIds,
          })
          this.plan = plan
          this.resolutions = this.defaultResolutions(plan)
          this.unresolvedByKey = {}
          this.extraNameTaken = []
          this.conflictIndex = 0
          this.final = null
          this.step = 2
        } catch (e) {
          this.handleError(e)
        } finally {
          this.loading = false
        }
      },
      next () {
        if (this.step === 2 && this.conflictTypes.length) {
          this.step = 3
          this.conflictIndex = 0
          return
        }
        if (this.step === 3 && this.conflictIndex < this.conflictTypes.length - 1) {
          this.conflictIndex++
          return
        }
        this.resolve()
      },
      back () {
        this.error = ''
        if (this.step === 3 && this.conflictIndex > 0) {
          this.conflictIndex--
          return
        }
        if (this.step === 4) {
          this.step = this.conflictTypes.length ? 3 : 2
          this.conflictIndex = Math.max(0, this.conflictTypes.length - 1)
          return
        }
        this.step--
      },
      async resolve () {
        this.loading = true
        this.error = ''
        try {
          const final = await this.importResolve({ token: this.plan.token, resolutions: this.resolutions })
          if (final.unresolved.length) {
            this.showUnresolved(final.unresolved)
            return
          }
          this.final = final
          this.deleteChecked = {}
          final.deleteIds.forEach((id) => {
            this.$set(this.deleteChecked, id, true)
          })
          this.stale = false
          this.step = 4
        } catch (e) {
          this.handleError(e)
        } finally {
          this.loading = false
        }
      },
      // решения не прошли повторную проверку (например, новое имя тоже занято) - назад к конфликту
      showUnresolved (unresolved) {
        const byKey = {}
        unresolved.forEach((u) => {
          byKey[u.key] = u.type === 'name_taken' ? 'Имя занято, например свободно: ' + u.suggestion : 'Нужно решение'
        })
        this.unresolvedByKey = byKey
        this.extraNameTaken = unresolved
          .filter((u) => u.type === 'name_taken')
          .map((u) => ({ key: u.key, name: u.name, takenBy: undefined, suggestion: u.suggestion }))
        const first = CONFLICT_ORDER.find((t) => unresolved.some((u) => u.type === t))
        this.step = 3
        this.conflictIndex = Math.max(0, this.conflictTypes.indexOf(first))
        this.error = 'Не все конфликты решены - проверьте отмеченные зоны'
      },
      async apply () {
        this.loading = true
        this.error = ''
        try {
          this.result = await this.importApply({
            token: this.plan.token,
            resolutions: this.resolutions,
            deleteIds: Object.keys(this.deleteChecked).filter((id) => this.deleteChecked[id]).map(Number),
          })
          this.step = 5
          this.$emit('imported', this.result)
        } catch (e) {
          this.handleError(e)
        } finally {
          this.loading = false
        }
      },
      handleError (e) {
        const body = e && e.response && e.response.data
        const reason = body && body.data && body.data.reason
        const message = (body && (body.fullErrorMessage || body.message)) || (e && e.message) || 'Ошибка импорта'
        if (reason === 'expired' || reason === 'foreign') {
          this.step = 1
          this.error = message
        } else if (reason === 'stale') {
          this.stale = true
          this.error = message
        } else if (reason === 'unresolved') {
          this.showUnresolved(body.data.unresolved || [])
        } else if (reason === 'bad_file' || (e && e.response && e.response.status === 422 && this.step === 1)) {
          this.error = this.validationMessage(body) || message
        } else {
          this.showErrorFromResponse(e)
        }
      },
      // ошибки валидации CRM лежат в data (поле → [сообщения])
      validationMessage (body) {
        const data = body && body.data
        if (!data || data.reason || typeof data !== 'object') return ''
        const first = Object.values(data)[0]
        return Array.isArray(first) ? first[0] : ''
      },
      openHistory () {
        this.$emit('open-history')
        this.$emit('input', false)
      },
      conflictKeys,
    },
  }
</script>

<style scoped>
.iz {
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 24px;
}
.iz-close {
  position: absolute;
  top: 14px;
  right: 14px;
}
.iz h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  text-align: center;
}
.iz-steps {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin: 14px 0 6px;
}
.iz-step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.4);
}
.iz-step .n {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid currentColor;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.iz-step.on {
  color: #ff9100;
  font-weight: 500;
}
.iz-step.done {
  color: #4caf50;
}
.iz-alert {
  margin: 12px 0 0;
  border-radius: 8px;
}
.iz-body {
  margin-top: 8px;
}
.iz-label {
  margin: 16px 0 8px;
  font-size: 13px;
  color: #6b7178;
}
.iz-drop {
  border: 1px dashed #c4cdd9;
  border-radius: 10px;
  padding: 12px;
  transition: background 0.15s, border-color 0.15s;
}
.iz-drop.over {
  border-color: #ff9100;
  background: rgba(255, 145, 1, 0.06);
}
.iz-file ::v-deep fieldset {
  border-radius: 8px;
}
.iz-hint {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
}
.iz-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 8px;
}
.iz-stat {
  background: #f7f8f9;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.iz-stat b {
  font-size: 18px;
  font-weight: 500;
}
.iz-stat span {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.25;
}
.iz-warn b {
  margin-left: 6px;
  color: #e65100;
}
.iz-warn-item {
  font-size: 13px;
  padding: 2px 0;
}
.iz-substeps {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 6px;
}
.iz-done {
  text-align: center;
  padding: 16px 0 4px;
}
.iz-done-title {
  margin-top: 8px;
  font-size: 16px;
  font-weight: 500;
}
.iz-done-text {
  margin-top: 6px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}
.iz-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}
.iz-btn {
  flex: 1;
  height: 44px !important;
  border-radius: 8px;
  font-size: 15px;
  text-transform: none;
  letter-spacing: 0;
}
.iz-btn.v-btn--outlined {
  border-color: #e0e3e7;
}
</style>
